import { routes } from '~constants/routes';
import { useAuthActionsContext } from '~context/auth';
import {
	NsfwReason,
	RegisterInboundByPath,
	RegisterOutboundByPath,
} from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useSwitchSafeModeWithAgeValidation } from '~features/nsfw';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';

import { navigationPaths } from './paths';
import type { NavigationLink } from './types';

export const useNavigationLinks = () => {
	const pathname = usePathname();

	const { isDemoUser, isSafeMode } = useSession();
	const { openRegister } = useAuthActionsContext();
	const router = useRouter();

	const updateSafeMode = useSwitchSafeModeWithAgeValidation();

	const annalsNavGridClicked = annals.navigation.useNavGridClicked({
		isAuthorizedZone: !isDemoUser,
	});
	const annalsNavigationClick = (path: string) => {
		switch (path) {
			case routes.explore.home:
				return annalsNavGridClicked;
			default:
				return () => undefined;
		}
	};

	const isActive = (path: string) => {
		if (path === routes.explore.home) {
			return Object.values(routes.explore).includes(pathname);
		}
		return pathname.startsWith(path);
	};

	const links: NavigationLink[] = [];
	navigationPaths.forEach((item) => {
		const { path, isDemo, isSafe, isDemoSafe } = item;

		const handle = (e: React.SyntheticEvent) => {
			annalsNavigationClick(path)();

			const cb = () => {
				router.push(path);
			};

			if (
				(!isDemo && isDemoUser) ||
				(isDemoUser && isSafeMode && !isDemoSafe)
			) {
				e.preventDefault();
				openRegister({
					cb,
					analytics: {
						inbound: RegisterInboundByPath[path],
						outbound: RegisterOutboundByPath[path],
					},
				});
			} else if (!isSafe) {
				e.preventDefault();
				if (isSafeMode) {
					void updateSafeMode(false, {
						force: true,
						nsfwReason: NsfwReason.NavigationLink,
						registerInbound: RegisterInboundByPath[path],
						registerOutbound: RegisterOutboundByPath[path],
					}).then(() => cb());
				} else {
					cb();
				}
			}
		};

		links.push({
			...item,
			active: isActive(path),
			handle,
		});
	});

	return links;
};
